import type { ComponentType } from 'react';
import { Box, Typography } from '@mui/material';
import type { SxProps, Theme } from '@mui/material';
import { HtmlField } from 'framework/ui/field';
import TimelineItemActions from '../actions/TimelineItemActions';
import TimelinePinAction from '../actions/TimelinePinAction';
import type { TimelineRecordAuthorProps } from '../types/configTypes';
import type { TimelineItem } from '../types/types';
import TimelineRecordLayout from './TimelineRecordLayout';

const metadataSx: SxProps<Theme> = {
  display: 'flex',
  flexDirection: 'column',
  flexWrap: 'wrap',
  borderTop: '1px solid',
  borderColor: 'border.separator',
  gap: 1,
  mt: 0.75,
  pt: 0.75,
};

interface TimelineRecordItemProps {
  item: TimelineItem;
  RecordAuthor: ComponentType<TimelineRecordAuthorProps>;
  isPinned: boolean;
  isHighlighted?: boolean;
  onPinned: (isPinned: boolean) => void;
}

const TimelineRecordItem = ({ item, RecordAuthor, isPinned, isHighlighted, onPinned }: TimelineRecordItemProps) => {
  const noteText = item.description;
  const pinAction = item.pinAction;
  const actions = (
    <TimelineItemActions
      timelineId={item.id}
      actions={item.actions}
      menuActions={item.menuActions}
      showAnchor={item.showAnchor ?? true}
      anchorBasePathname={item.anchorBasePathname}
      pinAction={
        pinAction ? (
          <TimelinePinAction
            isPinned={isPinned}
            disabled={pinAction.disabled}
            onChange={async nextPinned => {
              await pinAction.onChange(nextPinned);
              onPinned(nextPinned);
            }}
          />
        ) : undefined
      }
    />
  );

  return (
    <TimelineRecordLayout item={item} RecordAuthor={RecordAuthor} actions={actions} isHighlighted={isHighlighted}>
      {noteText && (
        <Box
          sx={{
            color: 'text.primary',
            mt: 0.5,
            '& p': {
              m: 0,
            },
          }}
        >
          <HtmlField value={noteText} />
        </Box>
      )}
      {item.content}
      {!!item.metadata?.length && (
        <Box sx={metadataSx}>
          {item.metadata.map(metadata => (
            <Typography key={metadata.id} variant="body1" color="text.secondary">
              {metadata.label}
              {': '}
              {metadata.value}
            </Typography>
          ))}
        </Box>
      )}
    </TimelineRecordLayout>
  );
};

export default TimelineRecordItem;
